import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { fmtDateShort } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ImpactBadge, WOTypeBadge } from '@/components/ui/status-badges';
import type { WorkOrder } from '@/types/maintenance';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useQuery } from '@tanstack/react-query';
import { fetchProfiles } from '@/lib/api/profiles';
import type { Profile } from '@/lib/api/profiles';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { queueAgeDays } from '@/lib/workOrderUtils';
import { useSpareParts } from '@/stores/sparePartsStore';
import { getAvailableStock } from '@/types/spareParts';

const IMPACT_ORDER: Record<string, number> = { line_down: 0, safety: 1, quality: 2, performance: 3, pm: 4 };
const MAX_CRITICAL = 8;
const MAX_READY = 6;
const MAX_BLOCKED = 6;

// Execution age: how long it has been actively worked (from start_time)
function execAgeDays(wo: WorkOrder): number {
  if (!wo.start_time) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(wo.start_time).getTime()) / 86400000));
}

function agingLabel(days: number): string {
  if (days === 0) return '<1d';
  return `${days}d`;
}

function AgingChip({ days }: { days: number; threshold: number }) {
  return (
    <span className={cn(
      'text-[11px] font-medium px-1.5 py-0.5 rounded',
      days > 5 && 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))]',
      days >= 3 && days <= 5 && 'bg-[hsl(var(--status-amber-bg))] text-[hsl(var(--status-amber))]',
      days < 3 && 'bg-muted text-muted-foreground',
    )}>
      {agingLabel(days)}
    </span>
  );
}

function useProfiles() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['profiles', user?.organization_id],
    queryFn: () => fetchProfiles(user?.organization_id ?? null),
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });
}

function AssigneeSelect({ wo, profiles, reassign }: {
  wo: WorkOrder;
  profiles: Profile[];
  reassign: (woId: string, userId: string) => Promise<void>;
}) {
  return (
    <Select
      value={wo.assigned_to || ''}
      onValueChange={(userId) => reassign(wo.id, userId).catch(() => toast.error('Failed to reassign'))}
    >
      <SelectTrigger className="h-5 text-[11px] border-0 bg-transparent p-0 pr-5 focus:ring-0 text-muted-foreground/60 hover:text-foreground w-auto min-w-[80px]">
        <SelectValue placeholder="Assign..." />
      </SelectTrigger>
      <SelectContent>
        {profiles.map(p => (
          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ============ SECTION SHELL ============
function SectionShell({ title, count, viewAllPath, showViewAll, children, className }: {
  title: string; count: number; viewAllPath: string; showViewAll: boolean; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={cn('bg-card rounded-lg border border-border/50', className)}>
      <div className="px-4 py-2.5 border-b border-border/50 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
          <span className="ml-1.5 text-foreground">{count}</span>
        </h3>
        {showViewAll && (
          <Link to={viewAllPath} className="text-[11px] text-primary hover:underline flex items-center gap-0.5">
            View All <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      <div className="p-3 space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-xs text-muted-foreground text-center py-6">{text}</p>;
}

// ============ CRITICAL & AGING ============
export function CriticalAgingSection({ workOrders }: { workOrders: WorkOrder[] }) {
  const { user } = useAuth();
  const { reassignWorkOrder } = useWorkOrders();
  const { data: profiles = [] } = useProfiles();
  const { settings } = useOrgSettings();
  const isManager = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';
  const threshold = settings.wo_aging_threshold_days;

  const critical = useMemo(() => {
    const open = workOrders.filter(wo => wo.status !== 'closed');
    return open.filter(wo =>
      wo.impact === 'line_down' ||
      wo.impact === 'safety' ||
      queueAgeDays(wo) >= threshold
    ).sort((a, b) => {
      const diff = (IMPACT_ORDER[a.impact ?? ''] ?? 9) - (IMPACT_ORDER[b.impact ?? ''] ?? 9);
      if (diff !== 0) return diff;
      return queueAgeDays(b) - queueAgeDays(a);
    });
  }, [workOrders, threshold]);
  const visible = critical.slice(0, MAX_CRITICAL);

  return (
    <SectionShell
      title="Critical & Aging"
      count={critical.length}
      viewAllPath="/maintenance/work-orders?filters=line_down"
      showViewAll={critical.length > MAX_CRITICAL}
    >
      {visible.length === 0 ? (
        <EmptyState text="No critical or aging work orders" />
      ) : (
        visible.map(wo => {
          const qAge = queueAgeDays(wo);
          const eAge = execAgeDays(wo);
          return (
            <div key={wo.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/40 transition-colors">
              <Link to={`/maintenance/work-orders/${wo.id}`} className="flex-1 min-w-0 block">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm">{wo.mold?.mold_id || wo.mold_id}</span>
                  <ImpactBadge impact={wo.impact} size="sm" />
                  <WOTypeBadge type={wo.type} size="sm" />
                  {wo.machine && (
                    <span className="text-[10px] text-muted-foreground/50">@ {wo.machine.name}</span>
                  )}
                </div>
                {wo.status === 'in_progress' && eAge > 0 && (
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                    active {agingLabel(eAge)} · open {agingLabel(qAge)}
                  </p>
                )}
                {wo.status === 'requested' && (
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                    waiting {agingLabel(qAge)}
                  </p>
                )}
                {wo.status === 'blocked' && (
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                    blocked {agingLabel(execAgeDays(wo))} · open {agingLabel(qAge)}
                  </p>
                )}
                {!isManager && wo.assignee && (
                  <p className="text-[11px] text-muted-foreground/60 mt-0.5">{wo.assignee.name}</p>
                )}
              </Link>
              {isManager && (
                <AssigneeSelect wo={wo} profiles={profiles} reassign={reassignWorkOrder} />
              )}
              <AgingChip days={qAge} threshold={threshold} />
            </div>
          );
        })
      )}
    </SectionShell>
  );
}

// ============ READY TO START ============
export function ReadyToStartSection({ workOrders }: { workOrders: WorkOrder[] }) {
  const { startWork, reassignWorkOrder } = useWorkOrders();
  const { user } = useAuth();
  const { data: profiles = [] } = useProfiles();
  const isManager = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';
  const [pendingStart, setPendingStart] = useState<WorkOrder | null>(null);

  const sorted = useMemo(() => {
    const requested = workOrders.filter(wo => wo.status === 'requested');
    // Sort by impact first, then oldest first — PM type no longer gets artificial priority
    return [...requested].sort((a, b) => {
      const impactDiff = (IMPACT_ORDER[a.impact ?? ''] ?? 9) - (IMPACT_ORDER[b.impact ?? ''] ?? 9);
      if (impactDiff !== 0) return impactDiff;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }, [workOrders]);
  const visible = sorted.slice(0, MAX_READY);

  function handleStartClick(wo: WorkOrder) {
    setPendingStart(wo);
  }

  async function confirmStart() {
    if (!pendingStart) return;
    try {
      await startWork(pendingStart.id);
    } catch {
      toast.error('Failed to start work order');
    } finally {
      setPendingStart(null);
    }
  }

  return (
    <>
      <SectionShell
        title="Ready to Start"
        count={sorted.length}
        viewAllPath="/maintenance/work-orders?filters=requested"
        showViewAll={sorted.length > MAX_READY}
      >
        {visible.length === 0 ? (
          <EmptyState text="No work orders waiting" />
        ) : (
          visible.map(wo => (
            <div key={wo.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/40 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={`/maintenance/work-orders/${wo.id}`} className="font-semibold text-sm text-primary hover:underline">
                    {wo.mold?.mold_id || wo.mold_id}
                  </Link>
                  <WOTypeBadge type={wo.type} size="sm" />
                  {wo.machine && (
                    <span className="text-[10px] text-muted-foreground/50">@ {wo.machine.name}</span>
                  )}
                  {isManager ? (
                    <AssigneeSelect wo={wo} profiles={profiles} reassign={reassignWorkOrder} />
                  ) : (
                    wo.assignee && <span className="text-[11px] text-muted-foreground/60">{wo.assignee.name}</span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                  {wo.failure_text || wo.problem_description}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[11px] px-2 shrink-0"
                onClick={() => handleStartClick(wo)}
              >
                Start Work
              </Button>
            </div>
          ))
        )}
      </SectionShell>

      {/* Kiosk confirmation dialog */}
      <Dialog open={!!pendingStart} onOpenChange={open => { if (!open) setPendingStart(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Start Work Order</DialogTitle>
          </DialogHeader>
          {pendingStart && (
            <p className="text-sm text-muted-foreground">
              Start <span className="font-semibold text-foreground">{pendingStart.mold?.mold_id || pendingStart.mold_id}</span>{' '}
              as <span className="font-semibold text-foreground">{user?.name}</span>?
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setPendingStart(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={confirmStart}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============ BLOCKED ============
const blockedReasonLabels: Record<string, string> = {
  parts: 'Parts',
  qa_hold: 'QA Hold',
  supplier: 'Supplier',
  waiting_approval: 'Approval',
  no_manpower: 'No Manpower',
  other: 'Other',
};

export function BlockedSection({ workOrders }: { workOrders: WorkOrder[] }) {
  const { reassignWorkOrder } = useWorkOrders();
  const { user } = useAuth();
  const { data: profiles = [] } = useProfiles();
  const { getPartsByMold } = useSpareParts();
  const isManager = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';

  function hasPartsAvailable(wo: WorkOrder): boolean {
    if (wo.blocked_reason !== 'parts') return false;
    const linked = getPartsByMold(wo.mold_id);
    return linked.some(({ part }) => getAvailableStock(part) > 0);
  }

  const sorted = useMemo(() => {
    const blocked = workOrders.filter(wo => wo.status === 'blocked');
    return [...blocked].sort((a, b) =>
      new Date(a.blocked_at || a.created_at).getTime() - new Date(b.blocked_at || b.created_at).getTime()
    );
  }, [workOrders]);
  const visible = sorted.slice(0, MAX_BLOCKED);

  function daysBlocked(blockedAt?: string): number {
    if (!blockedAt) return 0;
    return Math.max(1, Math.round((Date.now() - new Date(blockedAt).getTime()) / 86400000));
  }

  return (
    <SectionShell
      title="Blocked"
      count={sorted.length}
      viewAllPath="/maintenance/work-orders?tab=blocked"
      showViewAll={sorted.length > MAX_BLOCKED}
    >
      {visible.length === 0 ? (
        <EmptyState text="No blocked work orders" />
      ) : (
        <div className="grid gap-0.5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map(wo => {
            const days = daysBlocked(wo.blocked_at);
            return (
              <div
                key={wo.id}
                className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-muted/40 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/maintenance/work-orders/${wo.id}`}
                    className="font-semibold text-sm hover:underline block"
                  >
                    {wo.mold?.mold_id || wo.mold_id}
                  </Link>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {blockedReasonLabels[wo.blocked_reason || ''] || wo.blocked_reason_text || 'Blocked'}
                  </p>
                  {hasPartsAvailable(wo) && (
                    <p className="text-[10px] text-[hsl(var(--status-green))] font-medium">
                      Parts may be available
                    </p>
                  )}
                  {wo.expected_unblock_date && (
                    <p className="text-[10px] text-muted-foreground/60">
                      ETA {fmtDateShort(wo.expected_unblock_date)}
                    </p>
                  )}
                  {isManager && (
                    <AssigneeSelect wo={wo} profiles={profiles} reassign={reassignWorkOrder} />
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={cn(
                    'text-[11px] font-medium',
                    days > 3 ? 'text-[hsl(var(--status-red))]' : 'text-muted-foreground'
                  )}>
                    {days}d
                  </span>
                  <Link to={`/maintenance/work-orders/${wo.id}`} className="text-[11px] text-primary">Resolve →</Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionShell>
  );
}

// ============ TABBED EXECUTION FOCUS ============
export function ExecutionFocusTabs({ workOrders }: { workOrders: WorkOrder[] }) {
  const { settings } = useOrgSettings();
  const threshold = settings.wo_aging_threshold_days;

  const criticalCount = useMemo(() => {
    const open = workOrders.filter(wo => wo.status !== 'closed');
    return open.filter(wo =>
      wo.impact === 'line_down' || wo.impact === 'safety' || queueAgeDays(wo) >= threshold
    ).length;
  }, [workOrders, threshold]);

  const readyCount = workOrders.filter(wo => wo.status === 'requested').length;
  const blockedCount = workOrders.filter(wo => wo.status === 'blocked').length;

  function TabCount({ count, variant }: { count: number; variant: 'red' | 'amber' | 'muted' }) {
    if (count === 0) return null;
    const cls =
      variant === 'red'
        ? 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))]'
        : variant === 'amber'
        ? 'bg-[hsl(var(--status-amber-bg))] text-[hsl(var(--status-amber))]'
        : 'bg-muted text-muted-foreground';
    return (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
        {count}
      </span>
    );
  }

  return (
    <div>
      <h2 className="text-sm font-semibold mb-3">Execution Focus</h2>
      <Tabs defaultValue="critical">
        <TabsList className="h-8 w-full justify-start gap-0.5 p-0.5 mb-3">
          <TabsTrigger value="critical" className="h-7 px-3 text-xs flex items-center gap-1.5">
            Critical <TabCount count={criticalCount} variant="red" />
          </TabsTrigger>
          <TabsTrigger value="ready" className="h-7 px-3 text-xs flex items-center gap-1.5">
            Ready to Start <TabCount count={readyCount} variant="muted" />
          </TabsTrigger>
          <TabsTrigger value="blocked" className="h-7 px-3 text-xs flex items-center gap-1.5">
            Blocked <TabCount count={blockedCount} variant="amber" />
          </TabsTrigger>
        </TabsList>
        <TabsContent value="critical" className="mt-0">
          <CriticalAgingSection workOrders={workOrders} />
        </TabsContent>
        <TabsContent value="ready" className="mt-0">
          <ReadyToStartSection workOrders={workOrders} />
        </TabsContent>
        <TabsContent value="blocked" className="mt-0">
          <BlockedSection workOrders={workOrders} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
